<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiSegmented from '@/components/pos-v2/ui/PosUiSegmented.vue';
  import V2StatsExportButton from '@/components/v2/stats/V2StatsExportButton.vue';
  import { reservationStatsExportRows } from '@/domain/reservations/reservation-export';
  import { toDateInputValue } from '@/domain/stats/stats-date-range';
  import V2StatsDateRange from '@/components/v2/stats/V2StatsDateRange.vue';
  import V2StatsKpiCard from '@/components/v2/stats/V2StatsKpiCard.vue';
  import V2StatsChartCard from '@/components/v2/stats/V2StatsChartCard.vue';
  import V2BarChart from '@/components/v2/restaurant/dashboard/V2BarChart.vue';
  import V2ReservationStatusBadge from './V2ReservationStatusBadge.vue';
  import { useReservationReports } from '@/composition/reservations/use-reservation-reports';
  import { usePosMoney } from '@/composition/use-pos-money';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import type {
    TReservationSource,
    TReservationStatus,
  } from '@/interfaces/IReservation';
  const restaurant = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;
  const {
    range,
    allBranches,
    stats,
    loading,
    error,
    load,
  } = useReservationReports();
  const { formatComputedMoney } = usePosMoney();
  const t = (key: string) => String(proxy.$t(key));
  const branchOptions = computed(() => [
    { id: 'this', label: t('reservations.reports.thisBranch') },
    { id: 'all', label: t('reservations.reports.allBranches') },
  ]);
  const exportData = computed(() =>
    stats.value
      ? reservationStatsExportRows(stats.value, t)
      : { columns: [], rows: [] }
  );
  const exportFilename = computed(() =>
    range.value
      ? `reservas-${toDateInputValue(range.value.from)}-${toDateInputValue(
          range.value.to
        )}`
      : 'reservas'
  );
  const number = (value: number) =>
    value.toLocaleString(proxy.$i18n.locale, { maximumFractionDigits: 1 });
  const percent = (value: number) => `${number(value)}%`;
  const kpis = computed(() => {
    const totals = stats.value?.totals;
    return [
      { key: 'bookings', value: number(totals?.reservations ?? 0) },
      { key: 'covers', value: number(totals?.covers ?? 0) },
      { key: 'avgParty', value: number(totals?.avgParty ?? 0) },
      {
        key: 'online',
        value: number(totals?.online ?? 0),
        sublabel: percent(totals?.onlineShare ?? 0),
      },
      { key: 'walkIns', value: number(totals?.walkIns ?? 0) },
      {
        key: 'noShowRate',
        value: percent(totals?.noShowRate ?? 0),
        info: t('reservations.reports.info.noShowRate'),
        negative: (totals?.noShowRate ?? 0) > 10,
      },
      { key: 'cancelRate', value: percent(totals?.cancelRate ?? 0) },
      {
        key: 'deposits',
        value: formatComputedMoney(totals?.deposits.amount ?? 0),
        money: true,
      },
    ];
  });
  const sources: { key: TReservationSource; color: string }[] = [
    { key: 'online', color: '#0284c7' },
    { key: 'staff', color: '#737373' },
    { key: 'phone', color: '#a3a3a3' },
    { key: 'walk_in', color: '#d4d4d4' },
    { key: 'waitlist', color: '#525252' },
  ];
  const dayFormatter = computed(
    () =>
      new Intl.DateTimeFormat(proxy.$i18n.locale, {
        timeZone: 'UTC',
        day: 'numeric',
        month: 'short',
      })
  );
  const dailyChart = computed(() => ({
    labels: (stats.value?.perDay ?? []).map((day) =>
      dayFormatter.value.format(new Date(`${day.date}T12:00:00.000Z`))
    ),
    datasets: sources.map((source) => ({
      label: t(`reservations.source.${source.key}`),
      backgroundColor: source.color,
      data: (stats.value?.perDay ?? []).map(
        (day) => day.bySource[source.key] ?? 0
      ),
    })),
  }));
  const hourlyChart = computed(() => ({
    labels: (stats.value?.byHour ?? []).map(
      (band) => `${String(band.hour).padStart(2, '0')}:00`
    ),
    datasets: [
      {
        type: 'line',
        label: t('reservations.reports.chart.capacity'),
        data: (stats.value?.byHour ?? []).map((band) => band.capacity),
        borderColor: '#737373',
        backgroundColor: 'transparent',
        fill: false,
        lineTension: 0,
        pointRadius: 2,
        borderWidth: 2,
      },
      {
        type: 'bar',
        label: t('reservations.reports.kpi.covers'),
        data: (stats.value?.byHour ?? []).map((band) => band.covers),
        backgroundColor: '#0284c7',
      },
    ],
  }));
  const shiftChart = computed(() => ({
    labels: (stats.value?.perShift ?? []).map(
      (shift) => shift.name ?? t('reservations.list.noShift')
    ),
    datasets: [
      {
        label: t('reservations.reports.kpi.covers'),
        backgroundColor: '#0284c7',
        data: (stats.value?.perShift ?? []).map((shift) => shift.covers),
      },
    ],
  }));
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      position: 'bottom',
      labels: { boxWidth: 12, fontColor: '#525252' },
    },
    scales: {
      xAxes: [
        {
          gridLines: { display: false },
          ticks: { fontColor: '#737373', maxRotation: 45 },
        },
      ],
      yAxes: [
        {
          ticks: { beginAtZero: true, precision: 0, fontColor: '#737373' },
          gridLines: { color: '#f5f5f5' },
        },
      ],
    },
  };
  const stackedOptions = {
    ...chartOptions,
    scales: {
      xAxes: [{ ...chartOptions.scales.xAxes[0], stacked: true }],
      yAxes: [{ ...chartOptions.scales.yAxes[0], stacked: true }],
    },
  };
  const statuses: TReservationStatus[] = [
    'pending_payment',
    'pending',
    'confirmed',
    'arrived',
    'seated',
    'finished',
    'cancelled',
    'no_show',
  ];
  const outcomes = computed(() => {
    const total = Object.values(stats.value?.byStatus ?? {}).reduce(
      (sum, value) => sum + value,
      0
    );
    return statuses.map((status) => ({
      status,
      count: stats.value?.byStatus[status] ?? 0,
      percent: total ? (100 * (stats.value?.byStatus[status] ?? 0)) / total : 0,
    }));
  });
</script>
<template>
  <div class="reservation-reports-tab" :aria-busy="loading">
    <div class="reports-toolbar">
      <V2StatsDateRange
        v-if="restaurant.restaurant && restaurant.selectedBranch"
        :restaurant-id="restaurant.restaurant.id"
        :branch-id="restaurant.selectedBranch.id"
        storage-key="reservations"
        business-only
        @change="range = $event"
      />
      <div class="report-actions">
        <PosUiSegmented
          v-if="restaurant.branches && restaurant.branches.length > 1"
          :value="allBranches ? 'all' : 'this'"
          :items="branchOptions"
          @input="allBranches = $event === 'all'"
        />
        <V2StatsExportButton
          v-bind="exportData"
          :filename="exportFilename"
          :sheet-name="$t('reservations.tabs.reports')"
          :disabled="loading || error"
        />
      </div>
    </div>
    <div v-if="error" class="load-error" role="alert">
      <span>{{ $t('reservations.reports.loadError') }}</span
      ><PosUiButton
        color="neutral"
        variant="outline"
        :disabled="loading"
        @click="load"
        >{{ $t('reservations.page.retry') }}</PosUiButton
      >
    </div>
    <template v-else>
      <div class="kpi-grid">
        <V2StatsKpiCard
          v-for="kpi in kpis"
          :key="kpi.key"
          :label="String($t(`reservations.reports.kpi.${kpi.key}`))"
          :value="kpi.value"
          :sublabel="kpi.sublabel"
          :info="kpi.info"
          :tone="kpi.negative ? 'negative' : kpi.money ? 'positive' : 'default'"
          :loading="loading || !stats"
        />
      </div>
      <PosUiEmptyState
        v-if="stats && !stats.totals.reservations"
        icon="chart-line"
        :title="$t('reservations.reports.empty')"
      />
      <div v-else class="charts-grid">
        <V2StatsChartCard
          class="daily-chart"
          :title="String($t('reservations.reports.chart.coversPerDay'))"
          :loading="loading || !stats"
          :empty="!stats?.perDay.length"
          ><V2BarChart
            :chart-data="dailyChart"
            :options="stackedOptions"
            :height="280"
        /></V2StatsChartCard>
        <V2StatsChartCard
          :title="String($t('reservations.reports.chart.byHour'))"
          :loading="loading || !stats"
          :empty="!stats?.byHour.length"
          ><V2BarChart
            :chart-data="hourlyChart"
            :options="chartOptions"
            :height="280"
        /></V2StatsChartCard>
        <V2StatsChartCard
          :title="String($t('reservations.reports.chart.byShift'))"
          :loading="loading || !stats"
          :empty="!stats?.perShift.length"
          ><V2BarChart
            :chart-data="shiftChart"
            :options="chartOptions"
            :height="280"
        /></V2StatsChartCard>
        <V2StatsChartCard
          :title="String($t('reservations.reports.chart.byStatus'))"
          :loading="loading || !stats"
          :empty="!stats?.totals.reservations"
          ><div class="outcomes">
            <div
              v-for="outcome in outcomes"
              :key="outcome.status"
              class="outcome-row"
            >
              <div class="outcome-label">
                <V2ReservationStatusBadge :status="outcome.status" /><span
                  >{{ number(outcome.count) }} ·
                  {{ percent(outcome.percent) }}</span
                >
              </div>
              <div class="outcome-track">
                <span :style="{ width: `${outcome.percent}%` }" />
              </div>
            </div></div
        ></V2StatsChartCard>
      </div>
    </template>
  </div>
</template>
<style scoped lang="scss">
  .reservation-reports-tab {
    @apply tw-mx-auto tw-flex tw-h-full tw-min-h-0 tw-w-full tw-max-w-7xl tw-flex-col tw-gap-4 tw-overflow-y-auto tw-p-4 md:tw-p-6;
    > * {
      @apply tw-shrink-0;
    }
    .reports-toolbar {
      @apply tw-flex tw-flex-col tw-gap-3;
    }
    .report-actions {
      @apply tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-3;
    }
    .load-error {
      @apply tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-xl tw-border tw-border-red-200 tw-p-4 tw-text-sm tw-text-red-700;
    }
    .kpi-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      @apply tw-gap-3;
    }
    .charts-grid {
      @apply tw-grid tw-min-w-0 tw-grid-cols-1 tw-gap-4 xl:tw-grid-cols-2;
      > * {
        @apply tw-min-w-0;
      }
    }
    .daily-chart {
      @apply xl:tw-col-span-2;
    }
    .outcomes {
      @apply tw-flex tw-flex-col tw-gap-3;
    }
    .outcome-label {
      @apply tw-mb-1 tw-flex tw-items-center tw-justify-between tw-gap-2;
      > span {
        @apply tw-text-xs tw-tabular-nums tw-text-neutral-600;
      }
    }
    .outcome-track {
      @apply tw-h-1.5 tw-overflow-hidden tw-rounded-full tw-bg-neutral-100;
      span {
        @apply tw-block tw-h-full tw-rounded-full tw-bg-neutral-400;
      }
    }
  }
</style>
